import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSeo, breadcrumbLd } from "@/lib/seo";
import { EDITION } from "@/lib/edition";
import { ArrowLeft, X, Sparkle, GraduationCap, ArrowRight } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import ShareButton from "@/components/ShareButton";

const BACK = { exam: ["/exams", "All exams"], scholarship: ["/scholarships", "All scholarships"], discipline: ["/disciplines", "All fields"] };

export default function TopicDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [insts, setInsts] = useState([]);
  useEffect(() => {
    api.get(`/topics/${slug}`).then((r) => setT(r.data)).catch(() => setT(false));
    api.get(`/topics/${slug}/institutions`).then((r) => setInsts(r.data.items || [])).catch(() => setInsts([]));
  }, [slug]);
  useSeo(t && t !== false ? `${t.title} — Guide` : "Guide", t && t !== false ? t.summary : null, null,
    t && t !== false ? {
      canonicalPath: `/topics/${t.slug}`,
      type: "article",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: t.title,
          description: t.summary || undefined,
          about: t.type,
          publisher: { "@type": "Organization", name: EDITION.brand },
          mainEntityOfPage: `${EDITION.url}/topics/${t.slug}`,
        },
        breadcrumbLd([
          ["Home", "/"],
          [(BACK[t.type] || ["/", "Guides"])[1], (BACK[t.type] || ["/"])[0]],
          [t.title, `/topics/${t.slug}`],
        ]),
      ],
    } : undefined);

  if (t === null) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (t === false) return <div className="p-16 text-center text-muted-foreground">Guide not found.</div>;
  const [backTo, backLabel] = BACK[t.type] || ["/", "Back"];

  return (
    <article className="relative mx-auto max-w-3xl px-5 py-14" data-testid="topic-detail">
      <div className="mb-6 flex items-center justify-between">
        <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> {backLabel}</Link>
        <button onClick={() => (window.history.length > 2 ? navigate(-1) : navigate(backTo))} aria-label="Close" data-testid="topic-close-btn" className="grid h-9 w-9 place-items-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><X size={18} /></button>
      </div>
      <Badge variant="secondary" className="rounded-full font-normal capitalize">{t.type}</Badge>
      <h1 className="mt-3 font-heading text-4xl font-light leading-tight tracking-tight">{t.title}</h1>
      {t.summary && <p className="mt-3 text-muted-foreground">{t.summary}</p>}
      <div className="mt-4"><ShareButton entityType="article" entityId={t.slug} targetPath={`/topics/${t.slug}`} title={t.title} label="Share guide" /></div>

      <div className="prose mt-8 max-w-none">
        {(t.body || "").split("\n\n").map((block, i) => {
          const lines = block.split("\n").filter(Boolean);
          if (lines.length && lines.every((l) => l.startsWith("- "))) {
            return <ul key={i} className="mb-5 list-disc space-y-1.5 pl-5 text-foreground/90">{lines.map((l, j) => <li key={j} className="leading-relaxed">{l.slice(2)}</li>)}</ul>;
          }
          if (block.startsWith("## ")) {
            const [head, ...rest] = block.split("\n");
            const para = rest.join("\n").trim();
            return (
              <div key={i}>
                <h2 className="mb-3 mt-9 font-heading text-2xl font-light tracking-tight">{head.slice(3)}</h2>
                {para && <p className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90">{para}</p>}
              </div>
            );
          }
          return <p key={i} className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90">{block}</p>;
        })}
      </div>

      <p className="mt-10 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700"><Sparkle size={12} weight="fill" /> AI-assisted guide — always confirm current dates, fees and eligibility on the official website.</p>

      {insts.length > 0 && (
        <section className="mt-12 border-t border-border/60 pt-8" data-testid="topic-institutions">
          <h2 className="font-heading text-2xl font-light tracking-tight">Institutions on IndianEduPulse</h2>
          <p className="mt-1 text-sm text-muted-foreground">Explore institutions related to this {t.type}.</p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {insts.map((i) => (
              <Link key={i.id} to={`/institutions/${i.id}`} data-testid={`topic-inst-${i.id}`}
                className="group flex items-center gap-3 rounded-xl border border-border/60 p-3 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm">
                {i.logo_url
                  ? <img src={i.logo_url} alt={i.name} className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  : <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-green-50 to-orange-50"><GraduationCap size={18} weight="duotone" className="text-green-600/70" /></span>}
                <span className="min-w-0">
                  <span className="block truncate font-medium leading-tight text-foreground">{i.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{[i.type, i.location].filter(Boolean).join(" · ")}</span>
                </span>
              </Link>
            ))}
          </div>
          <Link to="/explore/institutions?country=India" className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="topic-browse-all">Browse all institutions <ArrowRight size={14} /></Link>
        </section>
      )}
    </article>
  );
}
